import React, { useState } from 'react';
import { Link } from 'react-router-dom'; // Imported Link component for navigation hooks
import { FiEye, FiWifi, FiBattery, FiChevronLeft, FiChevronRight, FiChevronDown, FiAlertTriangle } from 'react-icons/fi';
import { FaSnowflake } from 'react-icons/fa';
import { LuIceCreamCone } from 'react-icons/lu';
import sensorData from './sensorData.json';

// Helper Map dictionary to resolve raw data strings back to interactive React Icons elements
const iconMap = {
  snowflake: FaSnowflake,
  alert: FiAlertTriangle,
  icecream: LuIceCreamCone
};

const SensorTable = () => {
  const [activeTab, setActiveTab] = useState('All Sensors');
  const [currentPage, setCurrentPage] = useState(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const tabs = ['All Sensors', 'Chillers', 'Refrigerators', 'Freezers'];

  // 1. Dynamic list data filtering computation mapping plural tab names to singular type tags
  const filteredData = sensorData.filter((item) => {
    if (activeTab === 'All Sensors') return true;
    if (activeTab === 'Chillers') return item.type === 'Chiller';
    if (activeTab === 'Refrigerators') return item.type === 'Refrigerator';
    if (activeTab === 'Freezers') return item.type === 'Freezer';
    return true;
  });

  // Helper utility function to turn individual sensor items into matching plural dynamic URL route keys
  const getCategorySlug = (type) => {
    if (!type) return 'all';
    // Converts "Chiller" -> "chillers", "Refrigerator" -> "refrigerators", "Freezer" -> "freezers"
    return `${type.toLowerCase()}s`;
  };

  return (
    <div className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm font-sans select-none overflow-hidden">
      
      {/* Top Controls: Tabs and Category Dropdown */}
      <div className="flex items-center justify-between px-6 border-b border-gray-100 bg-[#FAF9F6] lg:bg-white pt-2 lg:pt-0">
        <div className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setCurrentPage(1); // Auto reset page tracking back to index 1 on item switches
              }}
              className={`py-4 text-[13px] font-bold whitespace-nowrap border-b-2 transition-all relative top-[1px] cursor-pointer ${
                activeTab === tab
                  ? 'border-[#0052FF] text-[#0052FF]'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="relative py-2.5">
          <button 
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 bg-white border border-gray-200 p-2 px-3 rounded-lg text-xs font-bold text-gray-700 shadow-[0_1px_3px_rgba(0,0,0,0.02)] cursor-pointer"
          >
            <span>All Categories</span>
            <FiChevronDown className="w-3.5 h-3.5 text-gray-400 stroke-[2.5]" />
          </button>
          {dropdownOpen && (
            <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-100 rounded-lg shadow-lg z-10 py-1 text-xs font-semibold text-gray-700">
              <div className="px-3 py-1.5 hover:bg-gray-50 cursor-pointer">Live Sensors</div>
              <div className="px-3 py-1.5 hover:bg-gray-50 cursor-pointer">Alert Status</div>
            </div>
          )}
        </div>
      </div>

      {/* 2. Main Table Segment Container with added space ("mt-4") right between your layout headers and list data */}
      <div className="w-full overflow-x-auto mt-4">
        <table className="w-full text-left border-collapse min-w-[900px]">
          <thead>
            <tr className="bg-[#FAF9F6]/80 text-[11px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100">
              <th className="py-4.5 px-6 font-bold">Sensor Name</th>
              <th className="py-4.5 px-4 font-bold">Location</th>
              <th className="py-4.5 px-4 font-bold">Type</th>
              <th className="py-4.5 px-4 text-center font-bold">Current Temp</th>
              <th className="py-4.5 px-4 text-center font-bold">Status</th>
              <th className="py-4.5 px-4 text-center font-bold">Signal</th>
              <th className="py-4.5 px-4 text-center font-bold">Battery</th>
              <th className="py-4.5 px-4 text-center font-bold">Last Seen</th>
              <th className="py-4.5 px-6 text-center font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100/70 text-[13px] font-semibold text-gray-700">
            {filteredData.map((row) => {
              const RowIcon = iconMap[row.iconKey] || FaSnowflake;
              return (
                <tr key={row.id} className="hover:bg-gray-50/40 transition-colors">
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${row.iconColor}`}>
                        <RowIcon className="w-4 h-4" />
                      </div>
                      <div className="space-y-0.5">
                        <p className="font-bold text-gray-900 text-[13.5px]">{row.name}</p>
                        <span className="text-[11px] font-bold text-gray-400 block uppercase">{row.code}</span>
                      </div>
                    </div>
                  </td>

                  <td className="py-4 px-4 text-gray-500 font-medium">{row.location}</td>
                  <td className="py-4 px-4 text-gray-400 font-medium">{row.type}</td>
                  <td className="py-4 px-4 text-center font-extrabold text-gray-900 text-[14px]">{row.temp}</td>
                  
                  <td className="py-4 px-4 text-center">
                    <span className={`inline-block text-[11px] font-bold px-2.5 py-0.5 rounded-md border ${
                      row.status === 'High Alert' 
                        ? 'bg-red-50 text-red-500 border-red-100' 
                        : 'bg-emerald-50 text-emerald-600 border-emerald-100'
                    }`}>
                      {row.status}
                    </span>
                  </td>

                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center gap-1.5 text-emerald-500">
                      <FiWifi className="w-4 h-4 stroke-[2.5]" />
                      <span className="text-gray-700 font-medium text-xs">{row.signal}</span>
                    </div>
                  </td>

                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center gap-1.5 text-gray-400">
                      <FiBattery className="w-4 h-4 rotate-90 stroke-[2.2]" />
                      <span className="text-gray-700 font-medium text-xs">{row.battery}</span>
                    </div>
                  </td>

                  <td className="py-4 px-4 text-center text-gray-500 font-medium text-xs">{row.lastSeen}</td>

                  {/* Actions Column: Changed raw layout action button to a React Router Link structure */}
                  <td className="py-4 px-6 text-center">
                    <Link 
                      to={`/categories/${getCategorySlug(row.type)}`}
                      className="p-2 bg-blue-50/50 hover:bg-blue-50 border border-blue-100/60 rounded-lg text-blue-600 transition-colors group cursor-pointer inline-flex items-center justify-center"
                    >
                      <FiEye className="w-4 h-4 stroke-[2.5]" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Re-built Bottom Footer Section with complete, validated JavaScript Array loops */}
      <div className="p-4 px-6 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white">
        <span className="text-xs font-semibold text-gray-400">
          Showing 1 to {filteredData.length} of {filteredData.length} sensors
        </span>

        <div className="flex items-center gap-1.5">
          <button 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer bg-white"
          >
            <FiChevronLeft className="w-4 h-4 stroke-[2.5]" />
          </button>
          
          {[1].map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => setCurrentPage(pageNum)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                currentPage === pageNum
                  ? 'bg-blue-50 border border-blue-200 text-[#0052FF]'
                  : 'border border-transparent text-gray-500 hover:bg-gray-50'
              }`}
            >
              {pageNum}
            </button>
          ))}

          <button 
            disabled={true}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 opacity-40 cursor-not-allowed bg-white"
          >
            <FiChevronRight className="w-4 h-4 stroke-[2.5]" />
          </button>
        </div>
      </div>

    </div>
  );
};

export default SensorTable;
